import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep, compact } from 'lodash';

import {
  isFailedRecomposeGenerationResult,
  isPartialGenerationResultId,
} from 'src/data/generation';
import { sortShortFormGenerationResults } from 'src/data/project';
import {
  addLanguageToGenerationResults,
  isDummyVariationId,
  VARIATION_RATING,
  VARIATION_STATUS,
  variationRatingTextToValue,
} from 'src/data/variation';
import { saveImageVariation } from 'src/graphql/generation';
import { queryGenerationResults } from 'src/graphql/project';
import {
  archiveVariations,
  deleteVariationRatings,
  publishVariations,
  rateVariation,
  updateAllProjectVariations,
  updateVariationImageMeta,
} from 'src/graphql/variation';
import { parseJson } from 'src/lib/common';
import { pluralize } from 'src/lib/format';
import { getParsedGraphQLFirstError, unwrapEdges } from 'src/lib/graphql';
import { captureMessage } from 'src/lib/sentry';

import { hasNonArchivedVariations } from '../../utils';

const INITIAL_STATE = {
  lastGenerationTimestamp: undefined,

  generationResults: [],
  landingPageAssetGenerationResults: [],
  loadingGenerationResults: false,
  loadingMoreGenerationResults: false,
  generationResultsLoaded: false,
  withPartialResults: false,

  editedVariationIds: [],
  detailsVisibleForVariationId: null,
  selectedVariationId: null,

  hiddenGenerationWarnings: [],

  didClearRatings: false,
  hasNextPage: null,
  nextPageCursor: null,
  variationWarnings: [],
  selectedFormatIdsByAssetType: {},
};

const listAdjust = (list, id, isAdding) =>
  isAdding ? [...list, id] : list.filter((n) => n !== id);

const addVariationAfterId = (generationResults, targetId, newVariations) => {
  return produce(generationResults, (draft) => {
    const stack = [];
    for (const result of draft) {
      stack.push(...result.variations);
      while (stack.length > 0) {
        const variation = stack.pop();

        if (variation.id === targetId) {
          // Add the new variations after the target id

          if (!variation?.variations) {
            variation.variations = [];
          }
          variation.variations = [...newVariations, ...variation.variations];
          return draft;
        }

        if (variation.variations && variation.variations.length > 0) {
          // Add nested variations to the stack
          stack.push(...variation.variations);
        }
      }
    }
  });
};

const prepareVariation = (variation) => {
  const imageMeta = variation.imageMeta ? parseJson(variation.imageMeta) : {};
  return {
    ...variation,
    imageMeta,
  };
};

const prepareResults = (results) => {
  return results.map((result) => {
    return {
      ...result,
      variations: result.variations.map((variation) => prepareVariation(variation)),
    };
  });
};

const clearRatingForNestedVariationsByGenerationTool = (variations, generationTool) => {
  return variations.map((variation) => {
    if (variation.generationTool === generationTool) {
      variation.rating = VARIATION_RATING.none;
      if (variation?.variations) {
        clearRatingForNestedVariationsByGenerationTool(variation.variations, generationTool);
      }
    }
    return variation;
  });
};

const updatePropertyInNestedVariationById = (variations, variationId, property, value) => {
  for (const variation of variations) {
    if (variation.id === variationId) {
      variation[property] = value;
    }
    if (variation?.variations) {
      updatePropertyInNestedVariationById(variation?.variations, variationId, property, value);
    }
  }

  return variations;
};

const updateNestedVariation = (variations, updatedVariation) => {
  for (const [index, variation] of variations.entries()) {
    if (variation.id === updatedVariation.id) {
      variations[index] = updatedVariation;
    }
    if (variation?.variations) {
      updateNestedVariation(variation?.variations, updatedVariation);
    }
  }

  return variations;
};

const includesNestedVariation = (variations, variationId) => {
  for (const variation of variations) {
    if (variation.id === variationId) {
      return true;
    }
    if (variation?.variations?.length) {
      if (includesNestedVariation(variation?.variations, variationId)) {
        return true;
      }
    }
  }
  return false;
};

export const PRIVATE_ACTIONS = {
  init:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  setVariationEdited:
    (variationId, value) =>
    ({ getState, setState }) => {
      const { editedVariationIds } = getState();
      const newList = listAdjust(editedVariationIds, variationId, value);
      setState({ editedVariationIds: newList });
    },

  updateVariationInGenerationResults:
    (variation) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();

      // Update variation in generationResults list
      const newResults = generationResults.map((generationResult) => {
        const variations = generationResult.variations.map((currentVariation) => {
          if (currentVariation.id === variation.id) {
            return {
              ...currentVariation,
              ...variation,
            };
          }
          return currentVariation;
        });

        return {
          ...generationResult,
          variations,
        };
      });
      setState({ generationResults: newResults });
    },
};

/**
 * Public available actions
 */
const ACTIONS = {
  queryGenerationResults:
    () =>
    ({ setState, getState, dispatch }, { project, notifications }) => {
      let isGenerating = false;

      setState({
        loadingGenerationResults: true,
        generationResults: [],
      });

      return queryGenerationResults(project.id)
        .then(({ edges, pageInfo }) => {
          const results = unwrapEdges(edges);
          if (results?.[0]?.isGenerating) {
            isGenerating = true;
            return results;
          }
          const preparedResults = prepareResults(results);
          const sorted = sortShortFormGenerationResults(preparedResults, { removeEmpty: true });
          const withLanguage = addLanguageToGenerationResults(sorted);

          setState({
            generationResults: withLanguage,
            nextPageCursor: pageInfo?.endCursor,
            hasNextPage: pageInfo?.hasNextPage,
          });
        })
        .catch((error) => {
          const parsedError = getParsedGraphQLFirstError(error);
          notifications.displayError(parsedError);
          setState({ generationResults: [] });
        })
        .finally(() => {
          if (!isGenerating) {
            setState({
              loadingGenerationResults: false,
              generationResultsLoaded: true,
            });
          }
        });
    },

  queryMoreGenerationResults:
    () =>
    async ({ setState, getState, dispatch }, { project, notifications }) => {
      const {
        nextPageCursor,
        hasNextPage,
        loadingGenerationResults,
        loadingMoreGenerationResults,
      } = getState();

      if (loadingGenerationResults || loadingMoreGenerationResults || !hasNextPage) {
        return;
      }

      setState({ loadingMoreGenerationResults: true });
      try {
        const { edges, pageInfo } = await queryGenerationResults(project.id, null, nextPageCursor);

        const results = unwrapEdges(edges);
        const preparedResults = prepareResults(results);
        const sorted = sortShortFormGenerationResults(preparedResults, { removeEmpty: true });
        const withLanguage = addLanguageToGenerationResults(sorted);
        dispatch(ACTIONS.addGenerationResults(withLanguage));
        setState({
          nextPageCursor: pageInfo?.endCursor,
          hasNextPage: pageInfo?.hasNextPage,
        });
      } catch (e) {
        notifications.displayError(e);
      } finally {
        setState({ loadingMoreGenerationResults: false });
      }
    },

  hideFlaggedVariation:
    (variationId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();

      // Search for "result" which contains specified variation
      const result = generationResults.find((r) => r.variations.some((v) => v.id === variationId));
      if (!result) {
        return;
      }

      const newVariations = result.variations
        // Try to remove variation from "parent" ones
        .filter((v) => v.id !== variationId);

      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  unHighlightVariations:
    (variationId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const result = generationResults.find((r) =>
        includesNestedVariation(r.variations, variationId)
      );

      if (!result) {
        return;
      }

      const newVariations = updatePropertyInNestedVariationById(
        result.variations,
        variationId,
        'shouldHighlight',
        false
      );
      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  deleteVariations:
    (ids) =>
    async ({ dispatch }, { notifications }) => {
      try {
        await archiveVariations({ ids });

        ids.forEach((id) => {
          dispatch(ACTIONS.archiveVariation(id));
        });
        const postfix = pluralize(ids.length, 'Variation', 'Variations');
        notifications.displaySuccess(`${postfix} deleted`);
      } catch (error) {
        notifications.displayError('Could not delete variations. Please try again.');
        captureMessage("Couldn't delete variation", {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'ListViewStore -> deleteVariations',
          },
        });
      }
    },

  archiveVariation:
    (variationId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();

      // Search for "result" which contains specified variation
      const result = generationResults.find((r) =>
        includesNestedVariation(r.variations, variationId)
      );

      if (!result) {
        return;
      }

      const newVariations = updatePropertyInNestedVariationById(
        result.variations,
        variationId,
        'status',
        VARIATION_STATUS.archived
      );

      const allDeleted = !hasNonArchivedVariations(result.variations);

      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
              allDeleted,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  markVariationEdited:
    (variationId) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.setVariationEdited(variationId, true));
    },

  setLastGeneratedTimestamp:
    (timestamp) =>
    ({ setState }) => {
      setState({
        lastGenerationTimestamp: timestamp,
      });
    },

  addGenerationResults:
    (results, options) =>
    ({ getState, setState, dispatch }) => {
      // check for failed recompose result to add variation warnings
      const recomposeWarnings = results.map((result) => {
        const firstVariation = result.variations?.[0];
        if (isFailedRecomposeGenerationResult(result) && firstVariation) {
          return {
            id: firstVariation.id,
            reason: result.mainFewerReceivedReason,
            blacklistMatches: result.blacklistMatches,
          };
        }

        return null;
      });
      dispatch(ACTIONS.addVariationWarnings(compact(recomposeWarnings)));

      const { generationResults, withPartialResults } = getState();
      const preparedResults = prepareResults(results);

      // Need to filter out in-progress "Generation Requests"
      // so we don't keep adding partial results to the list.
      const incomingIds = preparedResults.map((item) => item.id);
      const filtered = generationResults.filter(
        (item) => !incomingIds.includes(item.id) && !isPartialGenerationResultId(item.id)
      );

      if (options?.partialResult) {
        const newList = sortShortFormGenerationResults([...preparedResults, ...filtered]);

        setState({ generationResults: newList });
      } else {
        const results = sortShortFormGenerationResults([...preparedResults, ...filtered]);

        if (withPartialResults) {
          // Need to take variations with negative id
          const firstResult = results?.[0];
          const warnings = firstResult.variations
            .filter((variation) => isDummyVariationId(variation.id))
            .map((variation) => ({ id: variation.id }));

          dispatch(ACTIONS.addVariationWarnings(warnings));
        }

        setState({ generationResults: results, withPartialResults: false });
      }
    },

  addGenerationSubResult:
    (result) =>
    ({ getState, setState, dispatch }) => {
      const { generationResults } = getState();
      const resultsClone = [...generationResults];

      result.variations = result.variations.map((variation) => ({
        ...prepareVariation(variation),
        shouldHighlight: true,
      }));

      if (result?.variations?.length === 0 && result?.originalVariationId) {
        dispatch(
          ACTIONS.addVariationWarnings([
            {
              id: result.originalVariationId,
              reason: result.mainFewerReceivedReason,
              blacklistMatches: result.blacklistMatches,
            },
          ])
        );
      }

      const newList = addVariationAfterId(
        resultsClone,
        result.originalVariationId,
        result.variations
      );
      setState({
        generationResults: newList,
      });
    },

  clearGenerationResults:
    () =>
    ({ setState }) => {
      setState({
        generationResults: [],
      });
    },

  removeGenerationResult:
    (resultId) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const newResults = generationResults.filter((result) => result.id !== resultId);
      setState({ generationResults: newResults });
    },

  removePartialGenerationResults:
    (ids) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const newResults = generationResults
        .filter((result) => {
          if (result.originalRequestId && ids.includes(result.originalRequestId)) {
            return false;
          }
          return !ids.includes(result.id);
        })
        .map((result) => ({
          ...result,
          variations: result.variations.filter((variation) => !isDummyVariationId(variation.id)),
        }));
      setState({ generationResults: newResults });
    },

  updateVariation:
    (variation) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.updateVariationInGenerationResults(variation));
    },

  openVariationDetailsPanel:
    (variationId) =>
    ({ setState }) => {
      setState({ detailsVisibleForVariationId: variationId });
    },
  closeVariationDetailsPanel:
    () =>
    ({ setState }) => {
      setState({ detailsVisibleForVariationId: null });
    },
  isDetailsOpenedForVariationId:
    (id) =>
    ({ getState }) => {
      const { detailsVisibleForVariationId } = getState();
      return detailsVisibleForVariationId === id;
    },
  variationDetailsPanelVisible:
    () =>
    ({ getState }) => {
      const { detailsVisibleForVariationId } = getState();
      return !!detailsVisibleForVariationId;
    },

  selectVariation:
    (variationId) =>
    ({ setState }) => {
      setState({ selectedVariationId: variationId });
    },
  getSelectedVariationId:
    () =>
    ({ getState }) => {
      return getState().selectedVariationId;
    },

  changeClassifiedGenerationTool:
    (updatedVariation) =>
    ({ getState, setState }) => {
      const { generationResults } = getState();
      const result = generationResults.find((r) =>
        includesNestedVariation(r.variations, updatedVariation.id)
      );

      if (!result) {
        return;
      }

      const newVariations = updateNestedVariation(result.variations, updatedVariation);

      // Replace "result" object in array
      const newResults = generationResults.map((r) =>
        r.id === result.id
          ? {
              ...r,
              variations: newVariations,
            }
          : r
      );
      setState({ generationResults: newResults });
    },

  updateAllProjectVariations:
    ({ projectId, scoringMethod }) =>
    async ({ dispatch }, { notifications }) => {
      try {
        await updateAllProjectVariations({
          projectId,
          scoringMethod,
        });

        await dispatch(ACTIONS.queryGenerationResults());
      } catch (error) {
        notifications.displayError(error);
      }
    },

  rateVariation:
    (id, rating, variation, feedback) =>
    async ({ setState, getState }) => {
      const data = await rateVariation({
        id,
        rating: variationRatingTextToValue(rating),
        feedback,
      });

      const { generationResults } = getState();
      const newResults = updatePropertyInNestedVariationById(
        generationResults,
        id,
        'rating',
        rating
      );
      setState({ generationResults: newResults });

      return parseJson(data);
    },

  clearVariationRatings:
    (projectId, generationTool) =>
    async ({ getState, setState, dispatch }, { notifications }) => {
      try {
        await deleteVariationRatings({ projectId, generationTool });
        const { generationResults } = getState();
        const newResults = generationResults.map((generationResult) => {
          const variations = clearRatingForNestedVariationsByGenerationTool(
            generationResult.variations,
            generationTool
          );

          return {
            ...generationResult,
            variations,
          };
        });
        setState({ generationResults: newResults, didClearRatings: true });

        notifications.displaySuccess('Rating history cleared');
        return true;
      } catch (error) {
        notifications.displayError(error);
        return false;
      }
    },

  setDidClearRatings:
    (value) =>
    ({ setState }) => {
      setState({ didClearRatings: value });
    },

  updateVariationImageMeta: (id, imageMeta) => async () => {
    try {
      await updateVariationImageMeta({ id, imageMeta });
      return true;
    } catch {
      return false;
    }
  },

  addVariationWarnings:
    (newWarnings) =>
    ({ getState, setState }) => {
      const warnings = getState().variationWarnings;
      setState({
        variationWarnings: [...warnings, ...newWarnings],
      });
    },

  removeVariationWarning:
    (id) =>
    ({ setState, getState }) => {
      const warnings = getState().variationWarnings;
      const newValue = warnings.filter((item) => item.id !== id);
      setState({ variationWarnings: newValue });
    },

  saveImageVariation: (originalVariationId, encodedImage) => async () => {
    const imageVariation = await saveImageVariation({ originalVariationId, encodedImage });
    return imageVariation;
  },

  publishVariations:
    ({ variationIds, dataSource, accountId, assetName }) =>
    async ({ dispatch }) => {
      const result = await publishVariations({ variationIds, dataSource, accountId, assetName });
      result.forEach((updatedVariation) => {
        dispatch(
          PRIVATE_ACTIONS.updateVariationInGenerationResults({
            id: updatedVariation.id,
            exportInfo: updatedVariation.exportInfo,
          })
        );
      });
    },

  setSelectedFormatIdsForAssetType:
    (assetType, ids) =>
    ({ setState, getState }) => {
      const { selectedFormatIdsByAssetType } = getState();
      setState({
        selectedFormatIdsByAssetType: {
          ...selectedFormatIdsByAssetType,
          [assetType]: ids,
        },
      });
    },

  getSelectedFormatIdsForAssetType:
    (assetType) =>
    ({ getState }) => {
      const { selectedFormatIdsByAssetType } = getState();
      return selectedFormatIdsByAssetType[assetType] || null;
    },

  setWithPartialResults:
    (value) =>
    ({ setState }) => {
      setState({ withPartialResults: value });
    },
};

export const ListViewStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'document-list-view',
});
